import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import { Box, Button, CircularProgress, IconButton, List, Tooltip, Typography } from "@mui/material";
import { grey } from "../../../theme/muiColors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSocket } from "../../../context/SocketContext";
import { useUnreadMessagesContext } from "../../../context/unread-messages/UnreadMessagesContext";
import { resolveGroupChatName, resolveInternalChatName, resolveJobChatName, resolveSmsChatName, useChatDock } from "../../chat-dock";
import { CreateGroupModal, CreateSmsGroupModal, MessageGroupListItem, useMessageGroupsListQuery } from "../../message-groups";
import type { MessageGroupSummary } from "../../message-groups/messageGroups.types";
import { Contact, getContactId, ORGANIZATION_CONTACT_ROLES, useContactPersonStatus, useInternalContacts } from "../../internal-contacts";
import { filterEmployeeCompanyContacts } from "../../internal-contacts/_utils/employeeContacts.utils";
import { JobChatListItem, JobChatSummary, useActiveJobChatsQuery, useJobChatHistoryQuery, useJobChatsListSocket } from "../../job-chats";
import { SmsContactList } from "../contacts/components/SmsContactList";
import { useBulkResolveContacts } from "../contacts/hooks/useBulkResolveContacts";
import { useContactDetailsModal } from "../contacts/hooks/useContactDetailsModal";
import { useGetContacts } from "../contacts/hooks/useContacts";
import { useVirtualAssistant } from "../context/VirtualAssistantContext";
import { IContact } from "../types/contacts.types";
import { useCompanyNumberQuery } from "../widget/components/NumbersConfiguration/hooks/useCompanyNumberQuery";
import { InternalContactListItem } from "./components/InternalContactListItem";
import { ConversationType, SendNewSms } from "./components/SendNewSmS";
import { SmsChatGreeting } from "./components/SmsChatGreeting";
import { useSmsContactsQuery } from "./contacts/hooks/useSmsContactsQuery";
import { ContactCenterLoadingBody } from "../ContactCenterLoadingBody";

type InternalSectionKey =
  | "activeJobs"
  | "groups"
  | "smsGroups"
  | "workforces"
  | "organizationDirectory"
  | "employeeOrganization"
  | "smsConversations"
  | "contacts";

export function SmsWidget() {
  const { selectedContact: selectedSmsContact, handleSelectContact: setSelectedSmsContact, sessionNumber } = useVirtualAssistant();

  const { openChat, openChats, focusedKey, getChatKey } = useChatDock();

  const [contactType, setContactType] = useState<ConversationType>("all");
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [collapsedInternalSections, setCollapsedInternalSections] = useState<Record<InternalSectionKey, boolean>>({
    activeJobs: false,
    groups: false,
    smsGroups: false,
    workforces: false,
    organizationDirectory: false,
    employeeOrganization: false,
    smsConversations: false,
    contacts: false,
  });
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const { openContactDetails, openAddContactMenu } = useContactDetailsModal();
  const { company, roles, activeCompanyId } = useSelector((state: any) => state.userState);
  const role = company?.role?.type ?? roles?.[0]?.type;

  const { socket } = useSocket();
  // se usa /job-chats/active.
  const { data: activeJobChats } = useActiveJobChatsQuery();
  // se usa para /job-chats/history.
  const { data: jobChatsHistory } = useJobChatHistoryQuery();
  const focusedJobEventUid = useMemo(() => {
    const focused = openChats.find((chat) => chat.key === focusedKey);
    return focused?.target.type === "job" ? focused.target.jobEventUid : null;
  }, [openChats, focusedKey]);

  useJobChatsListSocket(socket, activeCompanyId, focusedJobEventUid);

  const { groups: messageGroups } = useMessageGroupsListQuery();
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isCreateSmsGroupOpen, setIsCreateSmsGroupOpen] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  // Elementos en la sección Jobs History
  const jobChatsHistoryItems = useMemo<JobChatSummary[]>(
    () => (jobChatsHistory?.items ?? []).filter((chat) => Number(chat.companyId) === Number(activeCompanyId)),
    [jobChatsHistory, activeCompanyId],
  );

  const historyJobEventUids = useMemo(() => new Set(jobChatsHistoryItems.map((chat) => chat.jobEventUid)), [jobChatsHistoryItems]);

  // Elementos para Active Jobs
  const activeJobChatItems = useMemo<JobChatSummary[]>(
    () =>
      (activeJobChats?.items ?? []).filter(
        (chat) => Number(chat.companyId) === Number(activeCompanyId) && !historyJobEventUids.has(chat.jobEventUid),
      ),
    [activeJobChats, activeCompanyId, historyJobEventUids],
  );

  const openChatKeys = useMemo(() => new Set(openChats.map((chat) => chat.key)), [openChats]);

  const isJobChatOpen = useCallback((jobEventUid: string) => openChatKeys.has(getChatKey({ type: "job", jobEventUid })), [openChatKeys, getChatKey]);

  const isInternalChatOpen = useCallback(
    (contact: Contact) => openChatKeys.has(getChatKey({ type: "internal", contactId: getContactId(contact) })),
    [openChatKeys, getChatKey],
  );

  const isSmsChatOpen = useCallback(
    (contact: { phoneNumber: string }) => openChatKeys.has(getChatKey({ type: "sms", phoneNumber: contact.phoneNumber })),
    [openChatKeys, getChatKey],
  );

  const isGroupChatOpen = useCallback((groupUid: string) => openChatKeys.has(getChatKey({ type: "group", groupUid })), [openChatKeys, getChatKey]);

  const { data: numbersData = [] } = useCompanyNumberQuery();

  const { data: contactsPages, isLoading: isLoadingSms, fetchNextPage, hasNextPage, isFetchingNextPage } = useSmsContactsQuery(sessionNumber);

  const { contacts: internalContacts, isLoading: isLoadingInternal } = useInternalContacts();
  const { getUnreadCount } = useUnreadMessagesContext();

  const smsContacts = useMemo(() => contactsPages?.pages?.flatMap((page) => page?.data ?? []) ?? [], [contactsPages]);

  const companyId = company?.id ? Number(company.id) : 0;
  const { data: contactsRes, isLoading: isLoadingContacts } = useGetContacts(companyId, 1, 100);

  const rawContacts: IContact[] = useMemo(() => {
    if (!contactsRes) return [];
    return (
      Array.isArray(contactsRes)
        ? contactsRes
        : (contactsRes as any)?.data
          ? (contactsRes as any).data
          : typeof contactsRes === "object" && (contactsRes as any).uid
            ? [contactsRes]
            : []
    ) as IContact[];
  }, [contactsRes]);

  const smsContactsWithoutChats = useMemo(() => {
    const smsPhoneDigits = new Set(smsContacts.map((sc) => sc.phoneNumber.replace(/\D/g, "")));
    return rawContacts
      .filter((contact) => {
        if (!contact.phoneNumbers || contact.phoneNumbers.length === 0) return false;
        return !contact.phoneNumbers.some((pn) => {
          const digits = pn.number.replace(/\D/g, "");
          return smsPhoneDigits.has(digits);
        });
      })
      .map((c) => {
        const primaryPhone = c.phoneNumbers?.find((pn) => pn.isPrimary) || c.phoneNumbers?.[0];
        const phoneNum = primaryPhone?.number || "";
        const smsContact = {
          phoneNumber: phoneNum,
          contact: c,
        };
        return smsContact;
      })
      .filter((c) => c.phoneNumber);
  }, [rawContacts, smsContacts]);

  const unresolvedPhones = useMemo(() => {
    return smsContacts.filter((contact) => !contact.contact).map((contact) => contact.phoneNumber);
  }, [smsContacts]);

  const { data: bulkResolvedMap = {} } = useBulkResolveContacts(unresolvedPhones);

  const resolvedSmsContacts = useMemo(() => {
    return smsContacts.map((contact) => {
      const resolved = contact.contact || bulkResolvedMap?.[contact.phoneNumber];
      if (resolved) {
        const isResolved = !contact.contact && !!bulkResolvedMap?.[contact.phoneNumber];
        return {
          ...contact,
          contact: isResolved ? { ...resolved, isResolved: true } : resolved,
        };
      }
      return contact;
    });
  }, [smsContacts, bulkResolvedMap]);

  const visibleInternalContacts = useMemo(() => internalContacts.filter((c) => !c.isBot && !c.isAmazonConnect && !c.isTwilio), [internalContacts]);

  const workforceContacts = useMemo(() => visibleInternalContacts.filter((c) => c.type === "workforce"), [visibleInternalContacts]);

  const organizationCoworkerContacts = useMemo(
    () => visibleInternalContacts.filter((c) => c.type === "organizationCoworker"),
    [visibleInternalContacts],
  );

  const isOrganizationMember = ORGANIZATION_CONTACT_ROLES.includes(role);
  const isEmployee = role === "employee";
  const employeeCompanyContacts = useMemo(
    () => filterEmployeeCompanyContacts(visibleInternalContacts, company?.id),
    [visibleInternalContacts, company?.id],
  );
  const { resolvePersonStatus } = useContactPersonStatus(isOrganizationMember || isEmployee);

  const isLoading = isLoadingSms || isLoadingInternal;

  // Sincroniza el contacto seleccionado con sus datos actualizados (incluye quedar desconocido al borrar).
  useEffect(() => {
    if (!selectedSmsContact) return;

    const normalizePhone = (phone?: string | null) => String(phone ?? "").replace(/\D/g, "");
    const selectedDigits = normalizePhone(selectedSmsContact.phoneNumber);

    const updated = resolvedSmsContacts.find((c: any) => {
      const digits = normalizePhone(c.phoneNumber);
      return digits === selectedDigits || digits.endsWith(selectedDigits) || selectedDigits.endsWith(digits);
    });

    if (!updated) return;

    const nextContact = updated.contact ?? null;
    const currentContact = selectedSmsContact.contact ?? null;
    if (JSON.stringify(nextContact) !== JSON.stringify(currentContact)) {
      setSelectedSmsContact({ ...updated, contact: nextContact });
    }
  }, [resolvedSmsContacts, selectedSmsContact, setSelectedSmsContact]);

  const handleContactsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;

    const bottomThreshold = 120;

    const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - bottomThreshold;

    if (!isNearBottom) return;
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;

    fetchNextPage();
  };

  const companyDisplayName = company?.businessName ?? "";

  const handleInternalContactSelect = useCallback(
    (contact: Contact | null) => {
      if (!contact) return;

      openChat(
        { type: "internal", contactId: getContactId(contact), contact },
        { companyName: companyDisplayName, chatName: resolveInternalChatName(contact) },
      );
    },
    [openChat, companyDisplayName],
  );

  const handleSmsContactSelect = useCallback(
    (contact: any) => {
      setSelectedSmsContact(contact);

      if (!contact?.phoneNumber) return;

      openChat(
        { type: "sms", phoneNumber: contact.phoneNumber, companyId: company?.id ?? null, sessionNumber, contact },
        { companyName: companyDisplayName, chatName: resolveSmsChatName(contact.phoneNumber, contact.contact) },
      );
    },
    [setSelectedSmsContact, openChat, companyDisplayName, company?.id, sessionNumber],
  );

  const handleGroupChatSelect = useCallback(
    (group: MessageGroupSummary | null) => {
      if (!group) return;

      openChat(
        { type: "group", groupUid: group.groupUid, companyId: group.companyId, group },
        { companyName: companyDisplayName, chatName: resolveGroupChatName(group) },
      );
    },
    [openChat, companyDisplayName],
  );

  const handleJobChatSelect = useCallback(
    (chat: JobChatSummary | null) => {
      if (!chat) return;

      openChat(
        { type: "job", jobEventUid: chat.jobEventUid, companyId: chat.companyId, chat },
        { companyName: companyDisplayName, chatName: resolveJobChatName(chat) },
      );
    },
    [openChat, companyDisplayName],
  );

  const canCreateGroups = !isEmployee;
  const internalGroups = useMemo(() => messageGroups.filter((group) => group.channel !== "sms"), [messageGroups]);
  const smsGroups = useMemo(() => messageGroups.filter((group) => group.channel === "sms"), [messageGroups]);
  const showGroupsSection = contactType === "all" || contactType === "internal" || contactType === "groups";
  const showSmsGroupsSection = contactType === "all" || contactType === "sms" || contactType === "groups";
  const isInternalSectionCollapsible = contactType === "all" || contactType === "internal";

  const toggleInternalSection = useCallback((section: InternalSectionKey) => {
    setCollapsedInternalSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  const renderInternalSectionHeader = ({
    label,
    count,
    section,
    leading,
    action,
  }: {
    label: string;
    count?: number;
    section: InternalSectionKey;
    leading?: React.ReactNode;
    action?: React.ReactNode;
  }) => {
    const isCollapsed = collapsedInternalSections[section];
    const header = (
      <Box
        role={isInternalSectionCollapsible ? "button" : undefined}
        tabIndex={isInternalSectionCollapsible ? 0 : undefined}
        aria-label={isInternalSectionCollapsible ? `${isCollapsed ? "Expand" : "Collapse"} ${label}` : undefined}
        onClick={isInternalSectionCollapsible ? () => toggleInternalSection(section) : undefined}
        onKeyDown={
          isInternalSectionCollapsible
            ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleInternalSection(section);
              }
            }
            : undefined
        }
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)",
          cursor: isInternalSectionCollapsible ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        {leading}
        {isInternalSectionCollapsible ? (
          isCollapsed ? (
            <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
          ) : (
            <ExpandLessRoundedIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
          )
        ) : null}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontSize: "0.65rem",
          }}
        >
          {count === undefined ? label : `${label} ${count}`}
        </Typography>
        <Box sx={{ flex: 1, height: "1px", bgcolor: "divider", opacity: 0.5 }} />
        {action}
      </Box>
    );

    return header;
  };

  const hasAnyContact =
    contactType === "all"
      ? smsContacts.length > 0 ||
      (isEmployee ? employeeCompanyContacts.length > 0 : visibleInternalContacts.length > 0) ||
      activeJobChatItems.length > 0 ||
      messageGroups.length > 0 ||
      smsContactsWithoutChats.length > 0
      : contactType === "events"
        ? activeJobChatItems.length > 0
        : contactType === "groups"
          ? messageGroups.length > 0 || canCreateGroups
          : contactType === "internal"
            ? internalGroups.length > 0 ||
            canCreateGroups ||
            (isEmployee ? employeeCompanyContacts.length > 0 : visibleInternalContacts.length > 0)
            : smsContacts.length > 0 || smsContactsWithoutChats.length > 0 || smsGroups.length > 0 || canCreateGroups;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <SendNewSms
        isSmsAvaileble={numbersData?.length > 0}
        selectedSmsContact={null}
        selectedInternalContact={null}
        selectedJobChat={null}
        handleSelectSmsContact={handleSmsContactSelect}
        handleSelectInternalContact={handleInternalContactSelect}
        handleSelectJobChat={handleJobChatSelect}
        handleSelectGroup={handleGroupChatSelect}
        messageGroups={messageGroups}
        canCreateGroups={canCreateGroups}
        onCreateGroup={() => setIsCreateGroupOpen(true)}
        onCreateSmsGroup={() => setIsCreateSmsGroupOpen(true)}
        internalContacts={visibleInternalContacts}
        smsContacts={resolvedSmsContacts}
        eventChannels={activeJobChatItems}
        contactType={contactType}
        onContactTypeChange={setContactType}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {isLoading && !hasAnyContact ? (
            <ContactCenterLoadingBody message="Loading conversations..." />
          ) : !hasAnyContact ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <SmsChatGreeting />
            </Box>
          ) : (
            <>
              <Box
                onScroll={handleContactsScroll}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 1,
                  py: 1,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "divider",
                    borderRadius: "3px",
                    "&:hover": { bgcolor: "action.disabled" },
                  },
                }}
              >
                {/* Sección de trabajos */}
                {(contactType === "all" || contactType === "events") && (activeJobChatItems.length > 0 || jobChatsHistoryItems.length > 0) && (
                  <>
                    {renderInternalSectionHeader({
                      label: "Active Jobs",
                      count: activeJobChatItems.length,
                      section: "activeJobs",
                      leading: (
                        <Box
                          role="status"
                          aria-label="Active jobs"
                          sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#22a447", flexShrink: 0 }}
                        />
                      ),
                    })}

                    {!isInternalSectionCollapsible || !collapsedInternalSections.activeJobs ? (
                      <>
                        {activeJobChatItems.length > 0 && (
                          <List disablePadding>
                            {activeJobChatItems.map((chat) => (
                              <JobChatListItem
                                key={chat.jobEventUid}
                                chat={chat}
                                isSelected={isJobChatOpen(chat.jobEventUid)}
                                currentTime={currentTime}
                                onSelect={handleJobChatSelect}
                                isEmployee={isEmployee}
                                companyDisplayName={company?.businessName}
                              />
                            ))}
                          </List>
                        )}

                        {jobChatsHistoryItems.length > 0 && (
                          <>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Tooltip
                                title={showJobHistory ? "Hide inactive job chat history" : "View inactive job chat history"}
                                arrow
                                slotProps={{ popper: { sx: { zIndex: "2147483647 !important" } } }}
                              >
                                <Button
                                  size="small"
                                  startIcon={<HistoryRoundedIcon sx={{ fontSize: "19px !important", color: grey[600] }} />}
                                  onClick={() => setShowJobHistory((visible) => !visible)}
                                  sx={{
                                    minWidth: 0,
                                    px: 0.75,
                                    fontWeight: 600,
                                    color: "text.secondary",
                                    textTransform: "none",
                                    fontSize: "0.72rem",
                                  }}
                                >
                                  {showJobHistory ? "Hide Jobs History" : "View Jobs History"}
                                </Button>
                              </Tooltip>
                              <Box sx={{ flex: 1, height: "1px", bgcolor: "divider", opacity: 0.5 }} />
                            </Box>

                            {showJobHistory && (
                              <List disablePadding>
                                {jobChatsHistoryItems.map((chat) => (
                                  <JobChatListItem
                                    key={chat.jobEventUid}
                                    chat={chat}
                                    isSelected={isJobChatOpen(chat.jobEventUid)}
                                    currentTime={currentTime}
                                    onSelect={handleJobChatSelect}
                                    isEmployee={isEmployee}
                                    companyDisplayName={company?.businessName}
                                  />
                                ))}
                              </List>
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                  </>
                )}

                {/* Sección de grupos internos */}
                {showGroupsSection && (internalGroups.length > 0 || canCreateGroups) && (
                  <>
                    {renderInternalSectionHeader({
                      label: "Groups Internals",
                      count: internalGroups.length,
                      section: "groups",
                      action: canCreateGroups ? (
                        <Tooltip title="New group" arrow>
                          <IconButton
                            size="small"
                            aria-label="New group"
                            onClick={(event) => {
                              event.stopPropagation();
                              setIsCreateGroupOpen(true);
                            }}
                            sx={{ p: 0.4, color: "text.secondary", "&:hover": { color: "warning.main" } }}
                          >
                            <PersonAddAlt1Icon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      ) : undefined,
                    })}

                    {!isInternalSectionCollapsible || !collapsedInternalSections.groups ? (
                      internalGroups.length > 0 ? (
                        <List disablePadding>
                          {internalGroups.map((group) => (
                            <MessageGroupListItem
                              key={group.groupUid}
                              group={group}
                              isSelected={isGroupChatOpen(group.groupUid)}
                              isEmployee={isEmployee}
                              onSelect={handleGroupChatSelect}
                            />
                          ))}
                        </List>
                      ) : (
                        <Typography sx={{ px: 2, pb: 1.5, fontSize: "0.72rem", color: grey[500] }}>
                          No groups yet. Create one to chat with several teammates at once.
                        </Typography>
                      )
                    ) : null}
                  </>
                )}

                {/* Sección de grupos por SMS */}
                {showSmsGroupsSection && (smsGroups.length > 0 || canCreateGroups) && (
                  <>
                    {renderInternalSectionHeader({
                      label: "Groups SMS",
                      count: smsGroups.length,
                      section: "smsGroups",
                      action: canCreateGroups ? (
                        <Tooltip title="New group text" arrow>
                          <IconButton
                            size="small"
                            aria-label="New group text"
                            onClick={(event) => {
                              event.stopPropagation();
                              setIsCreateSmsGroupOpen(true);
                            }}
                            sx={{ p: 0.4, color: "text.secondary", "&:hover": { color: "warning.main" } }}
                          >
                            <SmsRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      ) : undefined,
                    })}

                    {!isInternalSectionCollapsible || !collapsedInternalSections.smsGroups ? (
                      smsGroups.length > 0 ? (
                        <List disablePadding>
                          {smsGroups.map((group) => (
                            <MessageGroupListItem
                              key={group.groupUid}
                              group={group}
                              isSelected={isGroupChatOpen(group.groupUid)}
                              isEmployee={isEmployee}
                              onSelect={handleGroupChatSelect}
                            />
                          ))}
                        </List>
                      ) : (
                        <Typography sx={{ px: 2, pb: 1.5, fontSize: "0.72rem", color: grey[500] }}>
                          No group texts yet. Create one to chat with several numbers at once.
                        </Typography>
                      )
                    ) : null}
                  </>
                )}

                {/* Sección de contactos internos */}
                {(contactType === "all" || contactType === "internal") &&
                  isOrganizationMember &&
                  (workforceContacts.length > 0 || organizationCoworkerContacts.length > 0) && (
                    <>
                      {workforceContacts.length > 0 && (
                        <>
                          {renderInternalSectionHeader({
                            label: "Workforces",
                            count: workforceContacts.length,
                            section: "workforces",
                          })}

                          {!isInternalSectionCollapsible || !collapsedInternalSections.workforces ? (
                            <List disablePadding>
                              {workforceContacts.map((contact: Contact) => (
                                <InternalContactListItem
                                  key={getContactId(contact)}
                                  contact={contact}
                                  isSelected={isInternalChatOpen(contact)}
                                  unreadCount={getUnreadCount(getContactId(contact))}
                                  onSelect={handleInternalContactSelect}
                                  personStatus={resolvePersonStatus(contact)}
                                />
                              ))}
                            </List>
                          ) : null}
                        </>
                      )}

                      {organizationCoworkerContacts.length > 0 && (
                        <>
                          {renderInternalSectionHeader({
                            label: "Organization Directory",
                            count: organizationCoworkerContacts.length,
                            section: "organizationDirectory",
                          })}

                          {!isInternalSectionCollapsible || !collapsedInternalSections.organizationDirectory ? (
                            <List disablePadding>
                              {organizationCoworkerContacts.map((contact: Contact) => (
                                <InternalContactListItem
                                  key={getContactId(contact)}
                                  contact={contact}
                                  isSelected={isInternalChatOpen(contact)}
                                  unreadCount={getUnreadCount(getContactId(contact))}
                                  onSelect={handleInternalContactSelect}
                                  personStatus={resolvePersonStatus(contact)}
                                />
                              ))}
                            </List>
                          ) : null}
                        </>
                      )}
                    </>
                  )}

                {(contactType === "all" || contactType === "internal") && isEmployee && employeeCompanyContacts.length > 0 && (
                  <>
                    {renderInternalSectionHeader({
                      label: employeeCompanyContacts[0]?.businessName ?? "Organization",
                      count: employeeCompanyContacts.length,
                      section: "employeeOrganization",
                    })}

                    {!isInternalSectionCollapsible || !collapsedInternalSections.employeeOrganization ? (
                      <List disablePadding>
                        {employeeCompanyContacts.map((contact) => (
                          <InternalContactListItem
                            key={getContactId(contact)}
                            contact={contact}
                            isSelected={isInternalChatOpen(contact)}
                            unreadCount={getUnreadCount(getContactId(contact))}
                            onSelect={handleInternalContactSelect}
                            personStatus={resolvePersonStatus(contact)}
                          />
                        ))}
                      </List>
                    ) : null}
                  </>
                )}
                {/* Sección de contactos SMS */}
                {(contactType === "all" || contactType === "sms") && smsContacts.length > 0 && (
                  <>
                    {renderInternalSectionHeader({
                      label: "SMS Conversations",
                      count: resolvedSmsContacts.length,
                      section: "smsConversations",
                    })}

                    {!isInternalSectionCollapsible || !collapsedInternalSections.smsConversations ? (
                      <SmsContactList
                        contacts={resolvedSmsContacts}
                        isLoading={isLoading}
                        isContactSelected={isSmsChatOpen}
                        handleSelectContact={handleSmsContactSelect}
                        sessionNumber={sessionNumber}
                        onQuickSave={openAddContactMenu}
                        onEditContact={(contact) => {
                          openContactDetails(contact.phoneNumber, contact.contact);
                        }}
                      />
                    ) : null}
                  </>
                )}

                {/* Sección de contactos sin chats */}
                {(contactType === "all" || contactType === "sms") && smsContactsWithoutChats.length > 0 && (
                  <>
                    <Box sx={{ mt: smsContacts.length > 0 ? 2 : 0 }}>
                      {renderInternalSectionHeader({
                        label: "Contacts",
                        count: smsContactsWithoutChats.length,
                        section: "contacts",
                      })}
                    </Box>

                    {!isInternalSectionCollapsible || !collapsedInternalSections.contacts ? (
                      <SmsContactList
                        contacts={smsContactsWithoutChats}
                        isLoading={isLoadingContacts}
                        isContactSelected={isSmsChatOpen}
                        handleSelectContact={handleSmsContactSelect}
                        sessionNumber={sessionNumber}
                        onQuickSave={openAddContactMenu}
                        onEditContact={(contact) => {
                          openContactDetails(contact.phoneNumber, contact.contact);
                        }}
                      />
                    ) : null}
                  </>
                )}
                {isFetchingNextPage && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 1.5,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <CreateGroupModal
        open={isCreateGroupOpen}
        companyId={activeCompanyId}
        onClose={() => setIsCreateGroupOpen(false)}
        onCreated={handleGroupChatSelect}
      />

      <CreateSmsGroupModal
        open={isCreateSmsGroupOpen}
        companyId={activeCompanyId}
        onClose={() => setIsCreateSmsGroupOpen(false)}
        onCreated={handleGroupChatSelect}
      />
    </Box>
  );
}
